import { z } from "zod";

/**
 * Server-Sent Events carry **invalidation hints, never data**.
 *
 * The client turns an event into a query invalidation and re-reads through the ordinary
 * GET endpoint, so pushed state is by construction identical to fetched state — and no
 * event can deliver data the reader is not permitted to see, because the GET applies
 * their own permissions.
 */
export const SSE_EVENT_TYPES = {
  opportunityCreated: "opportunity.created",
  opportunityUpdated: "opportunity.updated",
  opportunityDeleted: "opportunity.deleted",
  clientCreated: "client.created",
  clientUpdated: "client.updated",
  userUpdated: "user.updated",
  /**
   * Configuration changed under everyone's feet.
   *
   * A stage rename relabels a board row for every open screen; a stalled-threshold change
   * reclassifies every open opportunity at once. Both are shared visual state, so both
   * have to reach other people's screens within the freshness window — the product has no
   * refresh affordance by design.
   */
  stageUpdated: "stage.updated",
  settingUpdated: "setting.updated",
  /**
   * A manager's board colour changed.
   *
   * Deliberately **narrow**, and deliberately not `user.updated`. The rest of `user.*` is
   * raised by administrative operations — invited, role changed, deactivated — and is sent
   * only to the Head of Sales, because the *timing* of administration is itself
   * administrative. A colour, by contrast, is painted on every board in the building, so
   * every board has to hear about it. One attribute, one event, no widening.
   */
  ownerUpdated: "owner.updated",
  /**
   * The stream is alive. Not an invalidation hint — the only member here that names no
   * record and invalidates nothing.
   *
   * It lives in this enum on purpose. The server also writes an SSE *comment* every
   * interval, but `EventSource` discards comment lines without dispatching anything, so the
   * client's staleness detector never heard them and every quiet board declared itself stale
   * after three intervals. A **named** event is what the browser actually delivers, and
   * registering it here is what gets a listener attached: the client iterates this enum, and
   * a named event nobody registered for is silently dropped — the same trap that killed
   * `stage.updated`, `setting.updated` and `owner.updated` on arrival.
   */
  heartbeat: "heartbeat",
} as const;

export type SseEventType = (typeof SSE_EVENT_TYPES)[keyof typeof SSE_EVENT_TYPES];

/**
 * Everything except the heartbeat: the types that carry an invalidation hint.
 *
 * The payload schema below is narrowed to these, so the mutation pipeline cannot publish a
 * heartbeat as though it named a changed record — the heartbeat has no entity, and therefore
 * no query root to invalidate.
 */
export type SseInvalidationEventType = Exclude<SseEventType, typeof SSE_EVENT_TYPES.heartbeat>;

const SSE_INVALIDATION_EVENT_TYPES = Object.values(SSE_EVENT_TYPES).filter(
  (type): type is SseInvalidationEventType => type !== SSE_EVENT_TYPES.heartbeat,
) as [SseInvalidationEventType, ...SseInvalidationEventType[]];

export const sseEventSchema = z.object({
  type: z.enum(SSE_INVALIDATION_EVENT_TYPES),
  entity: z.enum(["opportunity", "client", "user", "stage", "setting", "owner"]),
  id: z.string(),
  /** The row's version after the change, so a client can ignore what it already has. */
  version: z.number().int().optional(),
  /** Who caused it — lets a client skip invalidating what it just wrote itself. */
  actorId: z.string().uuid().optional(),
});

export type SseEvent = z.infer<typeof sseEventSchema>;

/**
 * The query-key root each entity's queries live under, declared rather than derived.
 *
 * This exists because deriving it was a real bug: `` `${entity}s` `` turns `opportunity`
 * into `opportunitys`, so every opportunity event matched nothing and the board never
 * updated live — silently, because the code read as if it worked. English plurals are not
 * a rule a cache key should depend on.
 */
/**
 * Several roots per entity, because configuration reaches further than its own screen: a
 * renamed stage changes what the *board* renders, and a changed stalled threshold changes
 * which cards are marked stalled. Invalidating only `stages` would leave every open board
 * showing the old labels until something else happened to refetch it.
 */
/**
 * `activity` appears on **every** entity, which is the one root here that is not about a
 * screen showing that entity.
 *
 * The company-wide feed is the single consumer that genuinely wants all of them: it is a view
 * of the audit store, and every mutation that raises an SSE event has just written a row into
 * it. A feed of "what is happening" that lags is a lie with a timestamp on it, and this
 * product has no refresh affordance by design — so the feed hears about everything, and the
 * role filter on the GET is what keeps that from showing anyone a row they may not read.
 */
export const SSE_ENTITY_QUERY_ROOT = {
  // `analytics` rides along on all three: the strip and the Analytics screen are the same
  // figures the board shows, so a change that moves a card has to move the numbers with it —
  // a stage rename relabels a distribution bar, and a stalled-threshold change moves the
  // Stalled tile. Decision Log row 34: analytics is live, on the same events the board uses.
  opportunity: ["opportunities", "analytics", "activity"],
  client: ["clients", "activity"],
  user: ["users", "activity"],
  stage: ["stages", "opportunities", "analytics", "activity"],
  setting: ["settings", "opportunities", "analytics", "activity"],
  // The colour rides the board's cards and the owner lookup the filters label with.
  owner: ["owners", "opportunities", "users", "activity"],
} as const satisfies Record<SseEvent["entity"], readonly string[]>;

/**
 * Heartbeat interval. No heartbeat for three intervals means the client is stale.
 *
 * Every interval the server writes both a `: keep-alive` comment (for intermediaries, which
 * never see the application layer) and an `event: heartbeat` frame (for the client, which
 * never sees comments). See `SSE_EVENT_TYPES.heartbeat`.
 */
export const SSE_HEARTBEAT_MS = 15_000;
export const SSE_STALE_AFTER_MS = 45_000;
